import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import { usePortfolioInstrumentQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioInstrumentQuery";
import type { ClientPortfolioProducerInstrumentResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { ProducerInstrumentFieldRows } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ProducerInstrumentFieldRows";
import { ToolDetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ToolDetailCard";
import { ToolHistorySection } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ToolHistorySection";
import { DetailCardSkeleton } from "web_myplann/Portfolio/Components/Redesign/Detail/Skeletons/DetailCardSkeleton";
import { normalizeInstrument } from "web_myplann/Portfolio/Helpers/normalizeInstrument";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

export type FundDetailSelection =
	| { kind: "tool"; toolId: number; name: string }
	| {
			kind: "producerInstrument";
			instrument: ClientPortfolioProducerInstrumentResponseDTO;
	  };

type Props = {
	open: boolean;
	onClose: () => void;
	portfolioId: number;
	selection?: FundDetailSelection;
	currencySign: string;
};

export const FundDetailDrawer: FC<Props> = ({
	open,
	onClose,
	portfolioId,
	selection,
	currencySign,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const toolId = selection?.kind === "tool" ? selection.toolId : undefined;

	const { data: instrument, isLoading } = usePortfolioInstrumentQuery(
		portfolioId,
		toolId,
	);
	const { data: reporting } = usePortfolioIdReportingQuery(
		toolId === undefined ? undefined : portfolioId,
	);

	const tool = normalizeInstrument(instrument);

	const name =
		selection?.kind === "producerInstrument"
			? (selection.instrument.name ?? selection.instrument.isin)
			: (tool?.name ?? selection?.name ?? "");

	return (
		<MyPlannDrawer
			open={open}
			onClose={onClose}
			title={t(Resources.PortfolioDetail.ToolDetail.title)}
		>
			<Stack pb={theme.spacingTokens.lg}>
				<Typography variant="bodyStrong" px={theme.spacingTokens.lg}>
					{name}
				</Typography>

				{selection?.kind === "producerInstrument" ? (
					<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
						<ProducerInstrumentFieldRows
							instrument={selection.instrument}
							currencySign={currencySign}
						/>
					</DetailCard>
				) : isLoading ? (
					<DetailCardSkeleton rows={6} />
				) : tool ? (
					<>
						<ToolDetailCard
							tool={tool}
							currencySign={currencySign}
							toolsSummary={reporting?.toolsSummary}
						/>
						<ToolHistorySection tool={tool} currencySign={currencySign} />
					</>
				) : null}
			</Stack>
		</MyPlannDrawer>
	);
};
